import { authFetch, BASE_BACKEND_URL, fetchWithAuth } from '../../auth'

import {
  ACTIVE_RELATIONS_PAGE_SIZE,
  buildUpdatePayloadForReassignment,
  dedupeRelations,
  requireSalesmanDbRef,
  STORE_RELATION_DEACTIVATION_ERRORS
} from './storeRelationDeactivationHelpers'

const RELATION_DETAIL_BATCH_SIZE = 10

const buildStoreRelationRequestError = async (response) => {
  const responseBody = await response.json().catch(() => ({}))
  const message =
    responseBody?.flow?.message ||
    responseBody?.errors?.[0] ||
    responseBody?.error ||
    `HTTP error ${response.status}`
  const error = new Error(message)

  error.status = response.status
  error.response = responseBody

  return error
}

const patchStoreRelation = async ({ relationId, payload }) => {
  const response = await authFetch(`${BASE_BACKEND_URL}/poc/store_relations_overview/${relationId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify(payload)
  })

  if (!response) return null

  if (!response.ok) {
    throw await buildStoreRelationRequestError(response)
  }

  return response.json().catch(() => ({}))
}

export const fetchActiveStoreRelationsForUser = async (salesmanDbRef) => {
  const normalizedSalesmanDbRef = salesmanDbRef?.trim()
  if (!normalizedSalesmanDbRef) return []

  let page = 1
  let totalRecords = 0
  const allResults = []

  do {
    const response = await fetchWithAuth(
      `${BASE_BACKEND_URL}/poc/store_relations_overview?page=${page}&per_page=${ACTIVE_RELATIONS_PAGE_SIZE}&user_uid=${encodeURIComponent(normalizedSalesmanDbRef)}&status=active`
    )
    const pageResults = Array.isArray(response?.results) ? response.results : []
    totalRecords = Number(response?.total_records ?? pageResults.length)

    allResults.push(...pageResults)
    page += 1

    if (!pageResults.length) {
      break
    }
  } while (allResults.length < totalRecords)

  return dedupeRelations(allResults)
}

export const fetchStoreRelationDetail = async (relationId) => {
  const response = await fetchWithAuth(`${BASE_BACKEND_URL}/poc/store_relations_overview/${relationId}`)
  return response?.result ?? null
}

const fetchStoreRelationDetailsInBatches = async (relationIds) => {
  const relationDetails = []

  for (let index = 0; index < relationIds.length; index += RELATION_DETAIL_BATCH_SIZE) {
    const relationIdsBatch = relationIds.slice(index, index + RELATION_DETAIL_BATCH_SIZE)
    const batchDetails = await Promise.all(
      relationIdsBatch.map(async (relationId) => {
        const relationDetail = await fetchStoreRelationDetail(relationId)

        if (!relationDetail) {
          throw new Error(`No fue posible cargar el detalle de la relacion ${relationId}.`)
        }

        return relationDetail
      })
    )

    relationDetails.push(...batchDetails)
  }

  return relationDetails
}

export const reassignActiveStoreRelationsForUser = async ({
  currentSalesmanDbRef,
  nextSalesman,
  activeRelations
}) => {
  const normalizedCurrentSalesmanDbRef = requireSalesmanDbRef(
    currentSalesmanDbRef,
    STORE_RELATION_DEACTIVATION_ERRORS.MISSING_CURRENT_SALESMAN_DB_REF
  )

  requireSalesmanDbRef(
    nextSalesman?.db_ref,
    STORE_RELATION_DEACTIVATION_ERRORS.MISSING_NEXT_SALESMAN_DB_REF
  )

  const relationsToProcess = dedupeRelations(activeRelations)
  const unsupportedRelation = relationsToProcess.find((relation) => !relation?.client_principal?.id)

  if (unsupportedRelation) {
    throw new Error(STORE_RELATION_DEACTIVATION_ERRORS.MISSING_EDITABLE_PRINCIPAL)
  }

  const relationIds = relationsToProcess
    .map((relation) => relation?.client_principal?.id)
    .filter(Boolean)

  if (!relationIds.length) {
    throw new Error(STORE_RELATION_DEACTIVATION_ERRORS.MISSING_EDITABLE_RELATION)
  }

  const relationDetails = await fetchStoreRelationDetailsInBatches(relationIds)

  const updates = relationDetails
    .map((relationDetail) => buildUpdatePayloadForReassignment({
      storeRelation: relationDetail,
      currentSalesmanDbRef: normalizedCurrentSalesmanDbRef,
      nextSalesman
    }))
    .filter(Boolean)

  if (!updates.length) {
    throw new Error(STORE_RELATION_DEACTIVATION_ERRORS.NO_REASSIGNMENT_CHANGES)
  }

  for (const update of updates) {
    await patchStoreRelation(update)
  }

  return updates.length
}
